import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { AskVolca } from "@/profitability/overview/ask-volca";
import type { RecordObject } from "@/profitability/records/objects";
import { SectionHeading } from "@/profitability/records/section-heading";
import {
  formatReportPeriod,
  reportError,
  runDates,
} from "@/profitability/utils";
import { FindingHeader } from "./finding-header";
import { FindingNote } from "./finding-note";
import { findingName } from "./finding-row";
import { FindingRuleActions } from "./finding-rules";
import { GroupedActivityFeed } from "./grouped-activity";
import { nextStepName } from "./labels";
import { useFinding } from "./query";
import { Rating } from "./rating";
import { useReviewSpec } from "./review-spec";
import { agentLine } from "./severity-mark";
import type { Finding } from "./types";

/**
 * The finding being judged: what was flagged, the money it is short, and
 * the decision. The record beside the page holds the figures, the weeks,
 * and the files. The list is how you reach the next finding.
 */
export const FindingPage = ({
  businessId,
  finding,
  object,
  detailsOpen = false,
  onOpenRecord,
  onAsk,
  onBack,
  chatOpen,
  canEditRule = false,
}: {
  businessId: string;
  finding: Finding;
  object: RecordObject | undefined;
  detailsOpen?: boolean;
  /** Opens or closes this finding's record. */
  onOpenRecord: () => void;
  onAsk: (question: string) => void;
  chatOpen: boolean;
  canEditRule?: boolean;
  /** Returns to the queue, when the queue and the finding take turns. */
  onBack?: () => void;
}) => {
  const spec = useReviewSpec();
  const detail = useFinding(businessId, finding.id);
  const name = findingName(finding, object) || (object?.name ?? "Record");
  const noun = (object?.name ?? "record").toLowerCase();
  const subject = /^\d/.test(name)
    ? `${object?.name ?? "Record"} ${name}`
    : name;
  const dates = runDates(finding);
  const context = [
    subject,
    finding.rule,
    dates ? formatReportPeriod(dates) : null,
  ]
    .filter(Boolean)
    .join(", ");
  const read = agentLine(finding.hypothesis);
  const step = finding.hypothesis?.action
    ? nextStepName(
        finding.hypothesis.action,
        finding.hypothesis.action_label,
        spec.next_steps,
      )
    : (finding.hypothesis?.action_label ?? null);

  return (
    <Stack
      gap={0}
      className="min-h-0 min-w-0 flex-1 pointer-coarse:[&_[data-slot=button]]:min-h-11 pointer-coarse:[&_[data-slot=button]]:min-w-11 pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
    >
      <Stack
        gap={6}
        className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-1 pt-1 pb-6"
      >
        <FindingHeader
          businessId={businessId}
          finding={finding}
          object={object}
          detailsOpen={detailsOpen}
          onOpenRecord={onOpenRecord}
          onBack={onBack}
        />
        {(read || step) && (
          <Stack gap={1} className="max-w-prose">
            <Text variant="caption" tone="muted">
              Volca's read
            </Text>
            {read && <Text className="max-w-prose text-pretty">{read}</Text>}
            {step && (
              <Text tone="muted" className="max-w-prose text-pretty">
                Next: {step}
              </Text>
            )}
          </Stack>
        )}
        {canEditRule && (
          <FindingRuleActions businessId={businessId} finding={finding} />
        )}
        <Stack
          direction="row"
          align="center"
          gap={3}
          className="border-t border-line pt-4"
        >
          <Rating
            businessId={businessId}
            subjectType="finding"
            subjectId={finding.id}
            runId={finding.run_id}
            feedback={finding.feedback}
            label={`${noun} ${name}`}
            noun={object ? noun : undefined}
          />
        </Stack>
        <section
          aria-label="Timeline"
          className="flex min-w-0 flex-col gap-3 [&_[data-slot=activity-feed-body]]:max-w-prose [&_[data-slot=activity-feed-body]]:text-pretty"
        >
          <SectionHeading>Timeline</SectionHeading>
          <FindingNote businessId={businessId} findingId={finding.id} />
          {detail.isPending && (
            <Skeleton className="h-20 w-full" aria-label="Loading activity" />
          )}
          {detail.isError && (
            <Stack gap={2}>
              <Text role="alert" tone="destructive">
                {reportError(detail.error)}
              </Text>
              <Button
                variant="secondary"
                className="self-start"
                loading={detail.isFetching}
                onClick={() => void detail.refetch()}
              >
                Try again
              </Button>
            </Stack>
          )}
          <GroupedActivityFeed
            activity={detail.data?.activity ?? []}
            hypothesis={finding.hypothesis}
          />
        </section>
      </Stack>
      <AskVolca
        hidden={chatOpen}
        placeholder="Ask Volca about this finding…"
        className="bottom-0 shrink-0 pt-3 pb-3"
        onAsk={(question) => onAsk(`About ${context}: ${question}`)}
      />
    </Stack>
  );
};

/** The finding, held until the queue names which one is first. */
export const FindingPending = () => (
  <Stack gap={6} className="min-h-0 min-w-0 flex-1" aria-busy role="status">
    <span className="sr-only">Loading the finding</span>
    <Stack gap={4} aria-hidden>
      <Stack direction="row" justify="between" gap={3}>
        <Skeleton className="h-7 w-32" />
        <Skeleton className="h-7 w-20" />
      </Stack>
      <Skeleton className="h-9 w-64 max-w-full" />
      <Skeleton className="h-4 w-48 max-w-full" />
      <Skeleton className="h-5 w-full max-w-prose" />
    </Stack>
    <Skeleton className="h-12 w-32" aria-hidden />
  </Stack>
);
